import React, { useEffect, useState } from "react"
import {
  Flex,
  FormControl,
  FormLabel,
  Box,
  HStack,
  useDisclosure,
  Text,
  type PopoverProps,
} from "@chakra-ui/react"
import { COLOR_SCHEMES } from "utilities/enums"
import { Control, useController } from "react-hook-form"
import { colorIsValid, colorWithHash } from "utilities/colors"
import ColorSelectPopover from "./ColorSelectPopover"
import ColorCircle from "./ColorCircle"

type Props = {
  name: string;
  control: Control<any>;
  label?: string;
  description?: React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  colorScheme?: "dark" | "light";
  compact?: boolean;
  maxW?: string | number;
  labelSize?: "sm" | "md" | "lg";
  labelWidth?: string | number;
  popoverPlacement?: PopoverProps["placement"];
}

const ColorSelect = ({
  name,
  control,
  label,
  description,
  disabled,
  error,
  colorScheme = "dark",
  compact,
  maxW,
  labelSize = "md",
  labelWidth = 1 / 5,
  popoverPlacement,
}: Props) => {
  const { field } = useController({ name, control })
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [color, setColor] = useState(colorWithoutHash(field.value))

  useEffect(() => {
    if (field.value !== colorWithHash(color) && colorIsValid(color)) {
      setColor(colorWithoutHash(field.value))
    }
  }, [field.value])

  const handleColorPickerChange = (newColor: string) => {
    if (colorIsValid(newColor)) {
      setColor(colorWithoutHash(newColor))
      field.onChange(colorWithHash(newColor))
    }
  }

  const handleTextInputChange = (newColor: string) => {
    setColor(colorWithoutHash(newColor))
    if (colorIsValid(newColor)) {
      field.onChange(colorWithHash(newColor))
    }
  }

  if (compact) {
    return (
      <FormControl isInvalid={error} isDisabled={disabled}>
        <Box>
          {label && <Text fontWeight="semibold" fontSize={labelSize}>{label}</Text>}
          {description && (
            <Text fontSize="md" mb={3}>
              {description}
            </Text>
          )}
          <Flex direction="column" align="start" w="100%" mt={description ? 0 : 4} maxW={maxW}>
            <ColorSelectPopover
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              onColorPickerChange={handleColorPickerChange}
              onTextInputChange={handleTextInputChange}
              color={color}
              compact={compact}
              disabled={disabled}
              placement={popoverPlacement}
            />
          </Flex>
        </Box>
      </FormControl>
    )
  }

  return (
    <FormControl isDisabled={disabled} mb={4} isInvalid={error}>
      <Flex>
        {label && <FormLabel pointerEvents="none" w={labelWidth}>{label}</FormLabel>}
        <Flex direction="column" align="start" w="100%">
          <HStack>{COLOR_SCHEMES[colorScheme!].map((c) => (
            <ColorCircle
              key={c}
              color={c}
              selected={color === c}
              onSelect={handleColorPickerChange}
            />
          ))}
          </HStack>
          <Flex>
            <ColorSelectPopover
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              onColorPickerChange={handleColorPickerChange}
              onTextInputChange={handleTextInputChange}
              color={color}
              compact={compact}
              placement={popoverPlacement}
            />
          </Flex>
        </Flex>
      </Flex>
    </FormControl>
  )
}

const colorWithoutHash = (color: string) => color.replace("#", "")

export default ColorSelect
